import React, { useEffect, useState } from 'react';
import { Container, Box, Grow, Grid, Typography, IconButton, Select, MenuItem, FormControl, InputLabel, useMediaQuery,useTheme } from '@mui/material';
import 'react-toastify/dist/ReactToastify.css';
import '../GlareEffect.css';
import FullScreenCarousel from './FullScreenCarousel';
import DeleteIcon from '@mui/icons-material/Delete';
import ResponsiveTabs from './ResponsiveTabs';

const apiUrl = 'http://wholesale.firehemp.com:5000';

function Inventory() {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [images, setImages] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [sortOption, setSortOption] = useState(''); // State to store selected sort option
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setIsAuthenticated(true);
    }

    fetchInventory();

    const intervalId = setInterval(() => {
      fetchInventory();
    }, 5000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    // Set `categoryTag` based on `tabValue`, using an array to include both 'CBD' and 'CBG' under the CBD tab.
    const categoryTag = tabValue === 0 ? ['THCA'] : ['CBD', 'CBG'];
  
    // Filter items by checking if any tag in `categoryTag` is included in `item.tag`
    let sortedItems = items.filter(item => 
      categoryTag.some(tag => item.tag.includes(tag))
    );
  
    // Apply sorting or additional filtering based on the selected sort option
    if (sortOption === 'All') {
      // If 'All' is selected, keep all items in the selected category
    } else {
      // Apply specific sorting or filter by tag
      switch (sortOption) {
        case 'Price Low':
          sortedItems = sortedItems.sort((a, b) => a.price - b.price);
          break;
        case 'Price High':
          sortedItems = sortedItems.sort((a, b) => b.price - a.price);
          break;
        case 'Exotic':
        case 'Indoor':
        case 'Greenhouse':
        case 'Outdoor':
        case 'Bigs':
        case 'Mids':
        case 'Mids/Smalls':
        case 'Smalls':
          sortedItems = sortedItems.filter(item => item.tag.includes(sortOption));
          break;
        default:
          break;
      }
    }
  
    setFilteredItems(sortedItems);
  }, [items, tabValue, sortOption]);
  

  const fetchInventory = async () => {
    try {
      const response = await fetch(`${apiUrl}/inventory`);
      const data = await response.json();
      setItems(data);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    }
  };

  const handleChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSortChange = (event) => {
    setSortOption(event.target.value); // Update selected sort option
  };

  const handleBoxClick = (item) => {
    const nonThumbnailImages = item.images.map(image => `${apiUrl}${image.substring(1)}`);
    setImages(nonThumbnailImages);
    setIsOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      try {
        const response = await fetch(`${apiUrl}/inventory/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          setItems(prevItems => prevItems.filter(item => item.id !== id));
          alert('Item deleted successfully.');
        } else {
          alert('Error deleting the item.');
        }
      } catch (error) {
        console.error('Error deleting the item:', error);
      }
    }
  };

  // Helper function to check if the release was within the last 24 hours
  const isNewRelease = (released) => {
    const currentTime = Math.floor(Date.now() / 1000);
    const timeDifference = currentTime - released;
    return timeDifference <= 86400;
  };

  // Function to map tag to color
  const getTagColor = (tag) => {
    switch (tag.toLowerCase()) {
      case 'exotic':
      case 'indoor':
      case 'outdoor':
      case 'greenhouse':
        return 'rgba(244,205,24,1)';
      default:
        return 'rgba(0,0,0,1)';
    }
  };

  const getTextColor = (tag) => {
    switch (tag.toLowerCase()) {
      case 'exotic':
      case 'indoor':
      case 'outdoor':
      case 'greenhouse':
        return 'rgba(0,0,0,1)';
      default:
        return 'rgba(244,205,24,1)';
    }
  };

  return (
    <Container>
<Box sx={{ width: '100%', bgcolor: 'black', mb: 2, display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'center' }}>
      <ResponsiveTabs tabValue={tabValue} handleChange={handleChange} />

      <FormControl 
        fullWidth={isMobile} 
        sx={{
          mt: isMobile ? 2 : 0, // Margin only on mobile
          ml: isMobile ? 0 : 'auto', // Aligns to right on desktop
          minWidth: isMobile ? '100%' : '200px' // Sets a fixed width on desktop
        }}
      >
        <InputLabel
          sx={{
            color: 'rgba(244,205,24,1)',
            '&.Mui-focused': {
              color: 'rgba(244,205,24,1)',
              textShadow: '0 0 4px black, 0 0 4px black, 0 0 4px black',
            },
          }}
        >
          Sort By
        </InputLabel>
        <Select
          value={sortOption}
          onChange={handleSortChange}
          sx={{
            color: 'white',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(244,205,24,1)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(244,205,24,1)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(244,205,24,1)',
            },
          }}
        >
          <MenuItem value="All">All</MenuItem>
          <MenuItem value="Price Low">Price Low</MenuItem>
          <MenuItem value="Price High">Price High</MenuItem>
          <MenuItem value="Exotic">Exotic</MenuItem>
          <MenuItem value="Indoor">Indoor</MenuItem>
          <MenuItem value="Greenhouse">Greenhouse</MenuItem>
          <MenuItem value="Outdoor">Outdoor</MenuItem>
          <MenuItem value="Bigs">Bigs</MenuItem>
          <MenuItem value="Mids">Mids</MenuItem>
          <MenuItem value="Mids/Smalls">Mids/Smalls</MenuItem>
          <MenuItem value="Smalls">Smalls</MenuItem>
        </Select>
      </FormControl>
    </Box>
      <Grid container spacing={2} justifyContent="center" wrap="wrap">
        {filteredItems.map((item, index) => (
          <Grow in={true} timeout={500 * (index + 1)} key={index}>
            <Grid item key={item.id}>
              <Box
                className="glare-box"
                sx={{
                  backgroundColor: '#313131',
                  padding: '0px',
                  borderRadius: '16px',
                  boxShadow: 3,
                  width: '256px',
                  height: '320px',
                  minWidth: '256px',
                  minHeight: '320px',
                  textAlign: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                }}
                onClick={() => handleBoxClick(item)}
              >
                {isNewRelease(item.released) && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '48px',
                      height: '48px',
                      zIndex: 1,
                    }}
                  >
                    <img
                      src={`${apiUrl}/images/new.png`}
                      alt="New"
                      style={{ width: '100%', height: '100%' }}
                    />
                  </Box>
                )}

                {isAuthenticated && (
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      zIndex: 2,
                      color: 'red',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(item.id);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}

                <img
                  src={`${apiUrl}${item.thumbnail.substring(1)}`}
                  alt={item.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />

                {item.tag && item.tag.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      position: 'absolute',
                      bottom: '42px',
                      right: '12px',
                      gap: '4px',
                      zIndex: 2,
                    }}
                  >
                    {item.tag.slice(0, 3).map((tag, tagIndex) => (
                      <Box
                        key={tagIndex}
                        sx={{
                          backgroundColor: getTagColor(tag),
                          color: '#fff',
                          minWidth: '40px',
                          borderRadius: '4px',
                          padding: '4px 12px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          textTransform: 'capitalize',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography sx={{ color: getTextColor(tag), fontSize: '10px', fontFamily: 'Montserrat, Arial, sans-serif' }}>
                          {tag}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    width: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    padding: '8px 16px',
                    boxSizing: 'border-box',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-end',
                      width: '100%',
                      color: '#fff',
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" noWrap>{item.name}</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      ${item.price}/lb
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grow>
        ))}
      </Grid>

      {isOpen && <FullScreenCarousel items={images} onClose={() => setIsOpen(false)} />}
        
    </Container>
  );
}

export default Inventory;
